import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { DomainHero, DomainBar } from "@/components/DomainSelector";
import { TelemetryFeed } from "@/components/TelemetryFeed";
import { LyapunovMeter } from "@/components/LyapunovMeter";
import { MetabolicGauge } from "@/components/MetabolicGauge";
import { ProcessFSM } from "@/components/ProcessFSM";
import { QuarantinePanel } from "@/components/QuarantinePanel";
import { HITLReview } from "@/components/HITLReview";
import { HardwareReadiness } from "@/components/HardwareReadiness";
import { AuditLog } from "@/components/AuditLog";
import { ArchitectureReference } from "@/components/ArchitectureReference";
import { Footer } from "@/components/Footer";
import { seedSim, stepSim, injectFault, recoveryStep, estimateRecovery, RECOVERY_DECAY, buildCrossCoupling } from "@/lib/simulation";
import { getAudit, createAudit, getKeys, createLibrary } from "@/lib/api";
import { signOperator } from "@/lib/operatorKey";

const fmtUptime = (s) => {
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
};

export default function Dashboard() {
  const [domain, setDomain] = useState(null);
  const [sim, setSim] = useState(null);
  const [quarantine, setQuarantine] = useState(false);
  const [pendingFault, setPendingFault] = useState(null);
  const [lastSealed, setLastSealed] = useState(null);
  const [audit, setAudit] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uptime, setUptime] = useState(0);
  const [pubkeys, setPubkeys] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [recoveryRemaining, setRecoveryRemaining] = useState(0);

  const quarantineRef = useRef(false);
  const recoveringRef = useRef(false);
  const domainRef = useRef(null);
  const faultAtRef = useRef(null);
  useEffect(() => {
    quarantineRef.current = quarantine;
  }, [quarantine]);
  useEffect(() => {
    recoveringRef.current = !!recovery;
  }, [recovery]);
  useEffect(() => {
    domainRef.current = domain;
  }, [domain]);

  // Load persisted audit ledger + Ed25519 public keys once.
  useEffect(() => {
    getAudit().then(setAudit).catch(() => {});
    getKeys().then(setPubkeys).catch(() => {});
  }, []);

  // Global uptime ticker.
  useEffect(() => {
    const id = setInterval(() => setUptime((u) => u + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // 2-second telemetry simulation loop (frozen during quarantine).
  useEffect(() => {
    if (!domain) return;
    const id = setInterval(() => {
      if (quarantineRef.current || recoveringRef.current) return; // frozen during hold & recovery ramp
      setSim((prev) => (prev ? stepSim(prev, domainRef.current) : prev));
    }, 2000);
    return () => clearInterval(id);
  }, [domain]);

  // Live post-approval recovery ramp: V(x) decays every 1s from the spike value
  // until it crosses back below the 1.00 threshold, then returns to nominal.
  useEffect(() => {
    if (!recovery) return;
    let v = recovery.spikeValue;
    let remaining = recovery.totalSeconds;
    setRecoveryRemaining(remaining);
    const id = setInterval(() => {
      v *= RECOVERY_DECAY;
      remaining = Math.max(0, remaining - 1);
      const recovered = v < 1.0;
      setSim((prev) => (prev ? recoveryStep(prev, recovered ? Math.min(v, 0.95) : v) : prev));
      setRecoveryRemaining(remaining);
      if (recovered) {
        clearInterval(id);
        setRecovery(null);
        setRecoveryRemaining(0);
        toast.success("System recovered · Nominal", {
          description: "V(x) < 1.00 — deterministic governor resumed.",
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [recovery]);

  const selectDomain = useCallback((d) => {
    setDomain(d);
    setSim(seedSim(d));
    setQuarantine(false);
    setPendingFault(null);
    setLastSealed(null);
    setRecovery(null);
    setRecoveryRemaining(0);
  }, []);

  const triggerFault = useCallback(() => {
    if (quarantine || !domain || !sim) return;
    faultAtRef.current = Date.now();
    const faultType = domain.faults[Math.floor(Math.random() * domain.faults.length)];
    const { sim: nextSim, spikeValue } = injectFault(sim, domain);
    const diagnosis = buildCrossCoupling(spikeValue);
    setSim(nextSim);
    setQuarantine(true);
    setPendingFault({
      faultType,
      spikeValue,
      diagnosis,
      governorAction:
        "Deterministic governor rejected the actuator command (V̇(x) > 0). Effector outputs clamped to laminar safe-holding profile; process advancement halted pending HITL sign-off.",
    });
    toast.error("Lyapunov violation intercepted — command quarantined", {
      description: faultType,
    });
  }, [quarantine, domain, sim]);

  const approveCorrection = useCallback(
    async (note) => {
      if (!pendingFault || !domain) return;
      setSubmitting(true);
      try {
        const operator = signOperator({
          domain: domain.name,
          fault_type: pendingFault.faultType,
          correction_note: note,
          lyapunov_value: pendingFault.spikeValue,
        });
        const entry = await createAudit({
          domain: domain.name,
          fault_type: pendingFault.faultType,
          correction_note: note,
          lyapunov_value: pendingFault.spikeValue,
          governor_action: pendingFault.governorAction,
          ...operator,
        });
        setAudit((prev) => [...prev, entry]);
        setQuarantine(false);
        setPendingFault(null);
        const holdDuration = faultAtRef.current
          ? Math.max(1, Math.round((Date.now() - faultAtRef.current) / 1000))
          : 2;
        setLastSealed({
          ...entry,
          spikeValue: pendingFault.spikeValue,
          holdDuration,
          threshold: 1.0,
          diagnosis: pendingFault.diagnosis,
          baselines: domain.baselines,
        });
        // Begin the live recovery ramp instead of snapping straight to nominal.
        setRecovery({
          spikeValue: pendingFault.spikeValue,
          totalSeconds: estimateRecovery(pendingFault.spikeValue).seconds,
        });
        toast.success("Correction sealed · Canonical", {
          description: "Ed25519 dual-key signature committed · recovery ramp engaged.",
        });
      } catch (e) {
        toast.error("Failed to seal correction", {
          description: "Could not reach the audit ledger service.",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [pendingFault, domain]
  );

  const resetSimulation = useCallback(() => {
    if (!domain) return;
    setSim(seedSim(domain));
    setQuarantine(false);
    setPendingFault(null);
    setLastSealed(null);
    setRecovery(null);
    setRecoveryRemaining(0);
    faultAtRef.current = null;
    toast.success("Simulation reset to nominal", {
      description: "Telemetry and governor restored · audit log preserved.",
    });
  }, [domain]);

  const saveToLibrary = useCallback(async (payload) => {
    try {
      await createLibrary(payload);
      toast.success("Saved to Analysis Library", {
        description: "View it under the Library tab.",
      });
    } catch (e) {
      toast.error("Could not save to library");
      throw e;
    }
  }, []);

  if (!domain || !sim) {
    return (
      <div className="min-h-screen control-grid-bg">
        <Header quarantine={false} uptime={fmtUptime(uptime)} pubkeys={pubkeys} />
        <DomainHero onSelect={selectDomain} />
        {audit.length > 0 && (
          <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 pb-10">
            <AuditLog entries={audit} onUpdateEntries={setAudit} />
          </div>
        )}
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen control-grid-bg">
      <Header quarantine={quarantine} recovering={!!recovery} uptime={fmtUptime(uptime)} domain={domain} pubkeys={pubkeys} />

      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h2 className="mono text-lg font-bold text-slate-100">{domain.name}</h2>
            <p className="mono text-[11px] text-slate-500 tracking-wide">
              {domain.code} · pre-seeded telemetry engaged
            </p>
          </div>
          <DomainBar active={domain} onSelect={selectDomain} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          <div className="col-span-full xl:col-span-3">
            <TelemetryFeed
              telemetry={sim.telemetry}
              sparks={sim.sparks}
              domain={domain}
              frozen={quarantine}
            />
          </div>
          <div className="col-span-full lg:col-span-6 xl:col-span-5">
            <LyapunovMeter
              vxSeries={sim.vxSeries}
              threshold={sim.threshold}
              quarantine={quarantine}
            />
          </div>
          <div className="col-span-full sm:col-span-6 lg:col-span-3 xl:col-span-2">
            <MetabolicGauge bt={sim.bt} />
          </div>
          <div className="col-span-full sm:col-span-6 lg:col-span-3 xl:col-span-2">
            <ProcessFSM fsmIndex={sim.fsmIndex} quarantine={quarantine} />
          </div>

          <div className="col-span-full">
            <HardwareReadiness />
          </div>

          <div className="col-span-full lg:col-span-6">
            <QuarantinePanel
              quarantine={quarantine}
              recovering={!!recovery}
              pendingFault={pendingFault}
              onTrigger={triggerFault}
              domain={domain}
              lastSealed={lastSealed}
              onReset={resetSimulation}
            />
          </div>
          <div className="col-span-full lg:col-span-6">
            <HITLReview
              pendingFault={pendingFault}
              domain={domain}
              onApprove={approveCorrection}
              submitting={submitting}
              lastSealed={lastSealed}
              onSaveLibrary={saveToLibrary}
              recovering={!!recovery}
              recoveryRemaining={recoveryRemaining}
              recoveryTotal={recovery?.totalSeconds ?? 0}
              currentVx={sim.vxSeries[sim.vxSeries.length - 1]?.vx ?? 0}
            />
          </div>

          <div className="col-span-full">
            <ArchitectureReference />
          </div>

          <div className="col-span-full">
            <AuditLog entries={audit} onUpdateEntries={setAudit} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
